/**
 * MemberManagePage - Members management at /company/:slug/members
 *
 * Lists all members with filters by status and team, invite code sharing,
 * team assignment via dropdown, role editing, and member removal.
 * BtoB-oriented language only (no fortune-telling or astrology references).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';
import billingService from '../../services/billingService';

/* ================================================================== */
/*  Constants                                                          */
/* ================================================================== */

const STATUS_ALL = 'all';
const TEAM_ALL = 'all';
const TEAM_UNASSIGNED = '__unassigned__';

const STATUS_CONFIG = {
  active: { colorKey: 'success', fallback: '#10B981' },
  invited: { colorKey: 'warning', fallback: '#F59E0B' },
  inactive: { colorKey: 'textMuted', fallback: '#6B7280' },
};

/* ================================================================== */
/*  Keyframes                                                          */
/* ================================================================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const toastSlide = keyframes`
  0%   { opacity: 0; transform: translateY(16px); }
  10%  { opacity: 1; transform: translateY(0); }
  90%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-8px); }
`;

/* ================================================================== */
/*  Styled components                                                  */
/* ================================================================== */

/* ---- Page shell ---- */

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  animation: ${fadeIn} 0.35s ease-out;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    padding: 24px 16px 64px;
  }
`;

/* ---- Toast ---- */

const Toast = styled.div`
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: ${(p) => p.theme.colors.success || '#10B981'};
  color: #fff;
  padding: 10px 24px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  font-size: 0.85rem;
  font-weight: 600;
  pointer-events: none;
  z-index: 1000;
  animation: ${toastSlide} 2s ease forwards;
`;

/* ---- Breadcrumb ---- */

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
`;

const BreadcrumbLink = styled(Link)`
  color: ${(p) => p.theme.colors.primary};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const BreadcrumbSep = styled.span`
  color: ${(p) => p.theme.colors.textSecondary};
  user-select: none;
`;

const BreadcrumbCurrent = styled.span`
  color: ${(p) => p.theme.colors.text};
  font-weight: 600;
`;

/* ---- Page title ---- */

const PageTitle = styled.h1`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 28px 0;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    font-size: 1.3rem;
  }
`;

/* ---- Invite section ---- */

const InviteSection = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  padding: 20px 24px;
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const InviteLabel = styled.span`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
  flex: 1;
  min-width: 0;
`;

const InviteCodeDisplay = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  font-family: ${(p) => p.theme.fonts?.mono};
  font-size: 1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  letter-spacing: 1px;
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.primary};
  color: #fff;
  border: none;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: ${(p) => p.theme.colors.secondary}; }
`;

/* ---- Filter bar ---- */

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const FilterSelect = styled.select`
  padding: 6px 12px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.text};
  font-size: 0.85rem;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: ${(p) => p.theme.colors.primary}; }
`;

/* ---- Table (desktop) ---- */

const TableWrapper = styled.div`
  overflow-x: auto;
  @media (max-width: ${(p) => p.theme.breakpoints?.tablet || '768px'}) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => p.theme.colors.textSecondary};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 14px;
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.text};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background 0.15s;
  cursor: pointer;
  &:hover { background: ${(p) => p.theme.colors.bgTertiary}; }
`;

/* ---- Cards (mobile) ---- */

const CardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 12px;

  @media (max-width: ${(p) => p.theme.breakpoints?.tablet || '768px'}) {
    display: flex;
  }
`;

const MemberCardStyled = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.2s, transform 0.15s;
  &:hover {
    border-color: ${(p) => p.theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;

const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSub = styled.div`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin-top: 2px;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

/* ---- Shared small elements ---- */

const Avatar = styled.div`
  flex-shrink: 0;
  width: ${(p) => (p.$size || 36)}px;
  height: ${(p) => (p.$size || 36)}px;
  border-radius: ${(p) => p.theme.borderRadius?.full || '9999px'};
  background: ${(p) => p.theme.colors.gradient || 'linear-gradient(135deg, #6366F1, #8B5CF6)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(p) => ((p.$size || 36) * 0.45)}px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: ${(p) => p.theme.borderRadius?.full || '9999px'};
  font-size: 0.72rem;
  font-weight: 600;
  background: ${(p) => p.$bg || 'rgba(99,102,241,0.12)'};
  color: ${(p) => p.$fg || '#6366F1'};
  white-space: nowrap;
`;

const TypeCodeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: ${(p) => p.theme.borderRadius?.full || '9999px'};
  background: ${(p) => p.theme.colors.primaryGlow || 'rgba(99,102,241,0.12)'};
  border: 1px solid ${(p) => p.theme.colors.primary};
  font-family: ${(p) => p.theme.fonts?.mono};
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.primary};
`;

const TeamSelect = styled.select`
  padding: 4px 8px;
  border-radius: ${(p) => p.theme.borderRadius?.sm || '4px'};
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.text};
  font-size: 0.82rem;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: ${(p) => p.theme.colors.primary}; }
`;

const ActionButton = styled.button`
  padding: 4px 10px;
  border-radius: ${(p) => p.theme.borderRadius?.sm || '4px'};
  background: transparent;
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.$danger ? (p.theme.colors.error || '#EF4444') : p.theme.colors.textSecondary};
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s, background 0.2s;
  &:hover {
    border-color: ${(p) => p.$danger ? (p.theme.colors.error || '#EF4444') : p.theme.colors.primary};
    color: ${(p) => p.$danger ? '#fff' : p.theme.colors.primary};
    background: ${(p) => p.$danger ? (p.theme.colors.error || '#EF4444') : 'transparent'};
  }
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/* ---- Confirm dialog overlay ---- */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
  padding: 24px;
`;

const Dialog = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  padding: 28px;
  max-width: 400px;
  width: 100%;
`;

const DialogTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 12px 0;
`;

const DialogText = styled.p`
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const DialogButton = styled.button`
  padding: 8px 20px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.2s;

  ${(p) =>
    p.$primary
      ? `
    background: ${p.$danger ? (p.theme.colors.error || '#EF4444') : p.theme.colors.primary};
    color: #fff;
    &:hover { opacity: 0.85; }
  `
      : `
    background: ${p.theme.colors.bgTertiary};
    color: ${p.theme.colors.text};
    border: 1px solid ${p.theme.colors.border};
    &:hover { border-color: ${p.theme.colors.primary}; }
  `}
`;

/* ---- Role edit dialog ---- */

const RoleSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.text};
  font-size: 0.9rem;
  margin-bottom: 16px;
  outline: none;
  &:focus { border-color: ${(p) => p.theme.colors.primary}; }
`;

/* ---- Empty state ---- */

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(p) => p.theme.colors.textSecondary};
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px dashed ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  font-size: 0.9rem;
  line-height: 1.6;
`;

/* ---- Loading skeleton ---- */

const SkeletonBlock = styled.div`
  background: linear-gradient(
    90deg,
    ${(p) => p.theme.colors.bgTertiary} 25%,
    ${(p) => p.theme.colors.bgSecondary} 50%,
    ${(p) => p.theme.colors.bgTertiary} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  height: ${(p) => p.$h || '20px'};
  width: ${(p) => p.$w || '100%'};
  margin-bottom: ${(p) => p.$mb || '0'};
`;

/* ---- Error state ---- */

const ErrorContainer = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.9rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 10px 24px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: ${(p) => p.theme.colors.primary};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: ${(p) => p.theme.colors.secondary}; }
`;

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

/* ================================================================== */
/*  Loading skeleton                                                   */
/* ================================================================== */

function LoadingSkeleton() {
  return (
    <PageContainer>
      <SkeletonBlock $h="16px" $w="180px" $mb="24px" />
      <SkeletonBlock $h="32px" $w="260px" $mb="28px" />
      <SkeletonBlock $h="72px" $w="100%" $mb="28px" />
      <SkeletonBlock $h="36px" $w="100%" $mb="20px" />
      {[0, 1, 2, 3, 4].map((i) => (
        <SkeletonBlock key={i} $h="48px" $w="100%" $mb="8px" />
      ))}
    </PageContainer>
  );
}

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */

function MemberManagePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth();

  /* ---- State ---- */
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastText, setToastText] = useState('');

  /* ---- Filters ---- */
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [deptFilter, setDeptFilter] = useState(STATUS_ALL);
  const [teamFilter, setTeamFilter] = useState(TEAM_ALL);

  /* ---- Dialogs ---- */
  const [confirmDelete, setConfirmDelete] = useState(null); // member obj
  const [editRole, setEditRole] = useState(null); // { member, newRole }
  const [billingStatus, setBillingStatus] = useState(null);

  /* ---- Derived ---- */
  const isAdmin = useMemo(
    () =>
      company?.members?.some(
        (m) => m.user_id === user?.id && m.role === 'admin'
      ) ?? false,
    [company, user]
  );

  const allTeams = useMemo(() => company?.teams || [], [company]);
  const allDepartments = useMemo(() => company?.departments || [], [company]);
  const allPositions = useMemo(() => {
    const fromSettings = company?.settings?.positions || [];
    // Also collect positions already used by members (covers custom ones)
    const fromMembers = members.map((m) => m.position).filter(Boolean);
    return [...new Set([...fromSettings, ...fromMembers])];
  }, [company, members]);

  /* ---- Toast helper ---- */
  const showToast = useCallback((msg) => {
    setToastText(msg);
    setTimeout(() => setToastText(''), 2200);
  }, []);

  /* ---- Data fetching ---- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [companyData, membersData] = await Promise.all([
        companyService.getCompany(slug),
        companyService.getMembers(slug),
      ]);
      setCompany(companyData);
      setMembers(membersData);
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---- Billing status ---- */
  useEffect(() => {
    if (slug) {
      billingService.getStatus(slug).then(setBillingStatus).catch(() => {});
    }
  }, [slug]);

  /* ---- Clipboard ---- */
  const copyInviteCode = useCallback(() => {
    if (!company?.invite_code) return;
    navigator.clipboard.writeText(company.invite_code).then(() => {
      showToast(t('招待コードをコピーしました', 'Invite code copied'));
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = company.invite_code;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showToast(t('招待コードをコピーしました', 'Invite code copied'));
    });
  }, [company, t, showToast]);

  /* ---- Department change (direct department_id) ---- */
  const handleDeptChange = useCallback(
    async (memberId, deptId) => {
      // Optimistic update
      const dept = (company?.departments || []).find((d) => d.id === deptId);
      setMembers((prev) =>
        prev.map((m) =>
          m.id === memberId
            ? { ...m, department_id: deptId || null, department_name: dept?.name || null }
            : m
        )
      );
      try {
        await companyService.updateMember(slug, memberId, {
          department_id: deptId || null,
        });
        showToast(t('部署を更新しました', 'Department updated'));
      } catch (err) {
        fetchData();
        showToast(err.message || t('更新に失敗しました', 'Update failed'));
      }
    },
    [slug, t, showToast, company, fetchData]
  );

  /* ---- Team change (with auto department sync) ---- */
  const handleTeamChange = useCallback(
    async (memberId, teamId) => {
      // Optimistic update: resolve department locally for instant UI feedback
      let optDeptId = null;
      let optDeptName = null;
      if (teamId) {
        for (const dept of (company?.departments || [])) {
          if (dept.teams?.some((tm) => tm.id === teamId)) {
            optDeptId = dept.id;
            optDeptName = dept.name;
            break;
          }
        }
      }
      setMembers((prev) =>
        prev.map((m) =>
          m.id === memberId
            ? { ...m, team_id: teamId || null, department_id: optDeptId ?? m.department_id, department_name: optDeptName ?? m.department_name }
            : m
        )
      );

      try {
        await companyService.updateMember(slug, memberId, {
          team_id: teamId || null,
        });
        showToast(t('チームを更新しました', 'Team updated'));
      } catch (err) {
        // Revert on failure
        fetchData();
        showToast(err.message || t('更新に失敗しました', 'Update failed'));
      }
    },
    [slug, t, showToast, company, fetchData]
  );

  /* ---- Role change ---- */
  const handleRoleConfirm = useCallback(async () => {
    if (!editRole) return;
    const { member, newRole } = editRole;
    try {
      await companyService.updateMember(slug, member.id, { role: newRole });
      setMembers((prev) =>
        prev.map((m) => (m.id === member.id ? { ...m, role: newRole } : m))
      );
      showToast(t('ロールを変更しました', 'Role updated'));
    } catch (err) {
      showToast(err.message || t('更新に失敗しました', 'Update failed'));
    } finally {
      setEditRole(null);
    }
  }, [editRole, slug, t, showToast]);

  /* ---- Position change ---- */
  const handlePositionSave = useCallback(
    async (memberId, newPosition) => {
      // Optimistic update
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, position: newPosition } : m))
      );
      try {
        await companyService.updateMember(slug, memberId, { position: newPosition });
        showToast(t('役職を更新しました', 'Position updated'));
      } catch (err) {
        fetchData();
        showToast(err.message || t('更新に失敗しました', 'Update failed'));
      }
    },
    [slug, t, showToast, fetchData]
  );

  /* ---- Drag & Drop reorder ---- */
  const dragItem = React.useRef(null);
  const dragOverItem = React.useRef(null);

  const handleDragStart = useCallback((idx) => {
    dragItem.current = idx;
  }, []);

  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    dragOverItem.current = idx;
  }, []);

  const handleDrop = useCallback(async () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return;

    const reordered = [...members];
    const [dragged] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, dragged);

    // Update local state immediately
    setMembers(reordered);

    dragItem.current = null;
    dragOverItem.current = null;

    // Persist to server
    try {
      await companyService.reorderMembers(slug, reordered.map((m) => m.id));
      showToast(t('表示順を更新しました', 'Order updated'));
    } catch (err) {
      showToast(err.message || t('並び替えに失敗しました', 'Reorder failed'));
      fetchData(); // revert on error
    }
  }, [members, slug, t, showToast, fetchData]);

  /* ---- Delete member ---- */
  const handleDeleteConfirm = useCallback(async () => {
    if (!confirmDelete) return;
    try {
      await companyService.deleteMember(slug, confirmDelete.id);
      setMembers((prev) => prev.filter((m) => m.id !== confirmDelete.id));
      showToast(t('メンバーを削除しました', 'Member removed'));
    } catch (err) {
      showToast(err.message || t('削除に失敗しました', 'Delete failed'));
    } finally {
      setConfirmDelete(null);
    }
  }, [confirmDelete, slug, t, showToast]);

  /* ---- Filtered members ---- */
  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      if (statusFilter !== STATUS_ALL && m.status !== statusFilter) return false;
      if (deptFilter !== STATUS_ALL) {
        if (deptFilter === TEAM_UNASSIGNED && m.department_id) return false;
        if (deptFilter !== TEAM_UNASSIGNED && m.department_id !== deptFilter) return false;
      }
      if (teamFilter === TEAM_UNASSIGNED && m.team_id) return false;
      if (
        teamFilter !== TEAM_ALL &&
        teamFilter !== TEAM_UNASSIGNED &&
        m.team_id !== teamFilter
      )
        return false;
      return true;
    });
  }, [members, statusFilter, deptFilter, teamFilter]);

  /* ---- Team name lookup ---- */
  const teamNameMap = useMemo(() => {
    const map = {};
    allTeams.forEach((tm) => {
      map[tm.id] = tm.name;
    });
    return map;
  }, [allTeams]);

  /* ================================================================ */
  /*  Render states                                                    */
  /* ================================================================ */

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{t('ログインが必要です', 'Login Required')}</ErrorTitle>
          <ErrorMessage>{t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.')}</ErrorMessage>
          <RetryButton onClick={() => navigate('/')}>
            {t('ログイン / 新規登録', 'Login / Sign Up')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (loading) return <LoadingSkeleton />;

  if (error) {
    const isAuthError = error === 'Not authenticated' || error === 'Invalid or expired token';
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{isAuthError ? t('ログインが必要です', 'Login Required') : t('エラーが発生しました', 'An Error Occurred')}</ErrorTitle>
          <ErrorMessage>{isAuthError ? t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.') : error}</ErrorMessage>
          <RetryButton onClick={isAuthError ? () => navigate('/') : fetchData}>
            {isAuthError ? t('ログイン / 新規登録', 'Login / Sign Up') : t('再試行', 'Retry')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  /* ================================================================ */
  /*  Main render                                                      */
  /* ================================================================ */

  return (
    <PageContainer>
      {toastText && <Toast>{toastText}</Toast>}

      {/* ──── Breadcrumb ──── */}
      <Breadcrumb>
        <BreadcrumbLink to={`/company/${slug}`}>
          {company?.name || t('組織', 'Company')}
        </BreadcrumbLink>
        <BreadcrumbSep>/</BreadcrumbSep>
        <BreadcrumbCurrent>{t('メンバー', 'Members')}</BreadcrumbCurrent>
      </Breadcrumb>

      <PageTitle>{t('メンバー管理', 'Member Management')}</PageTitle>

      {/* ──── Invite section ──── */}
      {company?.invite_code && (
        <InviteSection>
          <InviteCodeDisplay>{company.invite_code}</InviteCodeDisplay>
          <InviteLabel>
            {t(
              'このコードを共有してメンバーを招待',
              'Share this code to invite members'
            )}
          </InviteLabel>
          <CopyButton onClick={copyInviteCode}>
            {t('コピー', 'Copy')}
          </CopyButton>
        </InviteSection>
      )}

      {/* ──── Subscription limit notice ──── */}
      {isAdmin && billingStatus && billingStatus.plan === 'free' && billingStatus.active_members >= 3 && (
        <InviteSection style={{ borderColor: '#F59E0B', background: 'rgba(245,158,11,0.06)' }}>
          <InviteLabel style={{ color: '#F59E0B', fontWeight: 600 }}>
            {t(
              `無料枠（3人）に達しています。新規メンバーを追加するにはProプランへのアップグレードが必要です。`,
              `Free tier limit (3 members) reached. Upgrade to Pro to add more members.`
            )}
          </InviteLabel>
          <CopyButton
            style={{ background: '#F59E0B' }}
            onClick={() => navigate(`/company/${slug}/settings?tab=billing`)}
          >
            {t('アップグレード', 'Upgrade')}
          </CopyButton>
        </InviteSection>
      )}

      {/* ──── Filter bar ──── */}
      <FilterBar>
        <FilterLabel>{t('ステータス', 'Status')}:</FilterLabel>
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value={STATUS_ALL}>{t('すべて', 'All')}</option>
          <option value="active">{t('アクティブ', 'Active')}</option>
          <option value="invited">{t('招待中', 'Invited')}</option>
          <option value="inactive">{t('無効', 'Inactive')}</option>
        </FilterSelect>

        <FilterLabel>{t('部署', 'Dept')}:</FilterLabel>
        <FilterSelect
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
        >
          <option value={STATUS_ALL}>{t('すべて', 'All')}</option>
          {allDepartments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
          <option value={TEAM_UNASSIGNED}>
            {t('未所属', 'Unassigned')}
          </option>
        </FilterSelect>

        <FilterLabel>{t('チーム', 'Team')}:</FilterLabel>
        <FilterSelect
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
        >
          <option value={TEAM_ALL}>{t('すべて', 'All')}</option>
          {allTeams.map((tm) => (
            <option key={tm.id} value={tm.id}>
              {tm.name}
            </option>
          ))}
          <option value={TEAM_UNASSIGNED}>
            {t('未所属', 'Unassigned')}
          </option>
        </FilterSelect>
      </FilterBar>

      {/* ──── Members content ──── */}
      {filteredMembers.length === 0 ? (
        <EmptyState>
          {t(
            'フィルター条件に一致するメンバーがいません。',
            'No members match the current filters.'
          )}
        </EmptyState>
      ) : (
        <>
          {isAdmin && (
            <div style={{ fontSize: '0.8rem', color: '#999', marginBottom: 8 }}>
              {t(
                '※ 役職の上位順に表示されます。ドラッグ&ドロップで並び順を変更できます。この順序がダッシュボードや分析結果の表示順に反映されます。',
                'Members are displayed in position hierarchy order. Drag and drop to reorder. This order is reflected in dashboard and analysis views.'
              )}
            </div>
          )}

          {/* Desktop table */}
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>{t('名前', 'Name')}</Th>
                  <Th>{t('部署', 'Dept')}</Th>
                  <Th>{t('役職', 'Position')}</Th>
                  <Th>{t('チーム', 'Team')}</Th>
                  <Th>{t('タイプコード', 'Type Code')}</Th>
                  <Th>{t('ステータス', 'Status')}</Th>
                  {isAdmin && <Th>{t('操作', 'Actions')}</Th>}
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member, idx) => {
                  const globalIdx = members.indexOf(member);
                  return (
                    <MemberRow
                      key={member.id}
                      member={member}
                      teamNameMap={teamNameMap}
                      allTeams={allTeams}
                      allDepartments={allDepartments}
                      allPositions={allPositions}
                      isAdmin={isAdmin}
                      draggable={isAdmin}
                      onDragStart={() => handleDragStart(globalIdx)}
                      onDragOver={(e) => handleDragOver(e, globalIdx)}
                      onDrop={handleDrop}
                      onRowClick={() =>
                        navigate(`/company/${slug}/members/${member.id}`)
                      }
                      onDeptChange={handleDeptChange}
                      onTeamChange={handleTeamChange}
                      onPositionSave={handlePositionSave}
                      onEditRole={(m) =>
                        setEditRole({ member: m, newRole: m.role || 'member' })
                      }
                      onDelete={(m) => setConfirmDelete(m)}
                      t={t}
                    />
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>

          {/* Mobile cards */}
          <CardList>
            {filteredMembers.map((member) => (
              <MemberMobileCard
                key={member.id}
                member={member}
                teamNameMap={teamNameMap}
                isAdmin={isAdmin}
                onClick={() =>
                  navigate(`/company/${slug}/members/${member.id}`)
                }
                t={t}
              />
            ))}
          </CardList>
        </>
      )}

      {/* ──── Confirm delete dialog ──── */}
      {confirmDelete && (
        <Overlay onClick={() => setConfirmDelete(null)}>
          <Dialog onClick={(e) => e.stopPropagation()}>
            <DialogTitle>
              {t('メンバーを削除', 'Remove Member')}
            </DialogTitle>
            <DialogText>
              {t(
                `${confirmDelete.display_name || confirmDelete.email} をこの組織から削除しますか？この操作は取り消せません。`,
                `Remove ${confirmDelete.display_name || confirmDelete.email} from this organization? This action cannot be undone.`
              )}
            </DialogText>
            <DialogActions>
              <DialogButton onClick={() => setConfirmDelete(null)}>
                {t('キャンセル', 'Cancel')}
              </DialogButton>
              <DialogButton $primary $danger onClick={handleDeleteConfirm}>
                {t('削除', 'Remove')}
              </DialogButton>
            </DialogActions>
          </Dialog>
        </Overlay>
      )}

      {/* ──── Edit role dialog ──── */}
      {editRole && (
        <Overlay onClick={() => setEditRole(null)}>
          <Dialog onClick={(e) => e.stopPropagation()}>
            <DialogTitle>
              {t('ロールを変更', 'Change Role')}
            </DialogTitle>
            <DialogText>
              {editRole.member.display_name || editRole.member.email}
            </DialogText>
            <RoleSelect
              value={editRole.newRole}
              onChange={(e) =>
                setEditRole((prev) => ({ ...prev, newRole: e.target.value }))
              }
            >
              <option value="member">{t('メンバー', 'Member')}</option>
              <option value="admin">{t('管理者', 'Admin')}</option>
            </RoleSelect>
            <DialogActions>
              <DialogButton onClick={() => setEditRole(null)}>
                {t('キャンセル', 'Cancel')}
              </DialogButton>
              <DialogButton $primary onClick={handleRoleConfirm}>
                {t('保存', 'Save')}
              </DialogButton>
            </DialogActions>
          </Dialog>
        </Overlay>
      )}
    </PageContainer>
  );
}

/* ================================================================== */
/*  Sub-components                                                     */
/* ================================================================== */

/** Desktop table row for a member */
function MemberRow({
  member,
  teamNameMap,
  allTeams,
  allDepartments,
  allPositions,
  isAdmin,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  onRowClick,
  onDeptChange,
  onTeamChange,
  onEditRole,
  onPositionSave,
  onDelete,
  t,
}) {
  const firstLetter = member.display_name
    ? member.display_name.charAt(0)
    : '?';

  const statusLabel =
    member.status === 'active'
      ? t('アクティブ', 'Active')
      : member.status === 'invited'
      ? t('招待中', 'Invited')
      : t('無効', 'Inactive');

  const statusCfg = STATUS_CONFIG[member.status] || STATUS_CONFIG.active;

  // Resolve current department from member's team_id
  const currentDeptId = member.department_id || '';

  // Teams filtered by current department
  const teamsInDept = useMemo(() => {
    if (!currentDeptId) return allTeams;
    const dept = allDepartments.find((d) => d.id === currentDeptId);
    return dept?.teams || [];
  }, [currentDeptId, allDepartments, allTeams]);

  const handleDeptChange = (e) => {
    e.stopPropagation();
    const newDeptId = e.target.value;
    onDeptChange(member.id, newDeptId);
  };

  return (
    <Tr
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{ cursor: draggable ? 'grab' : undefined }}
    >
      <Td onClick={onRowClick}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {draggable && <span style={{ color: '#999', fontSize: '0.75rem', cursor: 'grab' }}>&#x2630;</span>}
          <Avatar $size={30}>{firstLetter}</Avatar>
          <span style={{ fontWeight: 600 }}>{member.display_name || '--'}</span>
        </div>
      </Td>
      <Td>
        {isAdmin ? (
          <TeamSelect
            value={currentDeptId}
            onChange={handleDeptChange}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">{t('未所属', 'Unassigned')}</option>
            {allDepartments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </TeamSelect>
        ) : (
          <span style={{ fontSize: '0.82rem' }}>{member.department_name || '--'}</span>
        )}
      </Td>
      <Td>
        {isAdmin ? (
          <TeamSelect
            value={member.position || ''}
            onChange={(e) => {
              e.stopPropagation();
              onPositionSave(member.id, e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">{t('未設定', 'Unset')}</option>
            {allPositions.map((pos) => (
              <option key={pos} value={pos}>{pos}</option>
            ))}
          </TeamSelect>
        ) : (
          <span style={{ fontSize: '0.82rem' }}>{member.position || '--'}</span>
        )}
      </Td>
      <Td>
        {isAdmin ? (
          <TeamSelect
            value={member.team_id || ''}
            onChange={(e) => {
              e.stopPropagation();
              onTeamChange(member.id, e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">{t('未所属', 'Unassigned')}</option>
            {teamsInDept.map((tm) => (
              <option key={tm.id} value={tm.id}>
                {tm.name}
              </option>
            ))}
          </TeamSelect>
        ) : (
          <span>{teamNameMap[member.team_id] || t('未所属', 'Unassigned')}</span>
        )}
      </Td>
      <Td onClick={onRowClick}>
        {member.type_code ? (
          <TypeCodeBadge>{member.type_code}</TypeCodeBadge>
        ) : (
          <span style={{ color: 'inherit', opacity: 0.4 }}>--</span>
        )}
      </Td>
      <Td onClick={onRowClick}>
        <StatusBadge $bg={`${statusCfg.fallback}20`} $fg={statusCfg.fallback}>
          {statusLabel}
        </StatusBadge>
      </Td>
      {isAdmin && (
        <Td>
          <ActionGroup>
            <ActionButton
              onClick={(e) => {
                e.stopPropagation();
                onEditRole(member);
              }}
            >
              {t('ロール', 'Role')}
            </ActionButton>
            <ActionButton
              $danger
              onClick={(e) => {
                e.stopPropagation();
                onDelete(member);
              }}
            >
              {t('削除', 'Remove')}
            </ActionButton>
          </ActionGroup>
        </Td>
      )}
    </Tr>
  );
}

/** Mobile card for a member */
function MemberMobileCard({ member, teamNameMap, isAdmin, onClick, t }) {
  const firstLetter = member.display_name
    ? member.display_name.charAt(0)
    : '?';

  const statusLabel =
    member.status === 'active'
      ? t('アクティブ', 'Active')
      : member.status === 'invited'
      ? t('招待中', 'Invited')
      : t('無効', 'Inactive');

  const statusCfg = STATUS_CONFIG[member.status] || STATUS_CONFIG.active;

  return (
    <MemberCardStyled onClick={onClick}>
      <CardTopRow>
        <Avatar $size={36}>{firstLetter}</Avatar>
        <CardInfo>
          <CardName>{member.display_name || '--'}</CardName>
          <CardSub>{member.position || '--'}</CardSub>
        </CardInfo>
      </CardTopRow>
      <CardMeta>
        <StatusBadge $bg={`${statusCfg.fallback}20`} $fg={statusCfg.fallback}>
          {statusLabel}
        </StatusBadge>
        {member.type_code && <TypeCodeBadge>{member.type_code}</TypeCodeBadge>}
        {member.department_name && (
          <span style={{ fontSize: '0.78rem', color: 'inherit', opacity: 0.65 }}>
            {member.department_name}
          </span>
        )}
        <span
          style={{
            fontSize: '0.78rem',
            color: 'inherit',
            opacity: 0.65,
          }}
        >
          {teamNameMap[member.team_id] || t('未所属', 'Unassigned')}
        </span>
      </CardMeta>
    </MemberCardStyled>
  );
}

export default MemberManagePage;
